import { OpportunityRow } from 'bindings/opportunities/OpportunityRow';
import * as React from 'react';
import styled from 'styled-components';
import { UserBlock } from '../../components_v2/dropdown/DropdownOwners';
import KanbanView, { KanbanCategory, KanbanItem } from '../../components_v2/kanban/kanban';
import { DateAccessor } from '../client-companies/data/CompanyColumns';
import { TOOLBARS_HEIGHT } from '../globals/defaultToolbar/style/Style';
import { FlexDiv } from '../products/style';
import { formatCurrency } from '../reports/utils';
import { QueryUpdateOpportunity } from './data/Data';
import { ProbabilityBadge } from './data/OpportunityColumns';
import { OpportunityStatus } from './model/Model';

export default function OpportunityKanban(props: {
	opportunities: OpportunityRow[]
	statuses: OpportunityStatus[]
	onOpen: (id: number) => void
	updateOpportunity: (id: number, params: QueryUpdateOpportunity) => Promise<void>
	onUpdateError: () => void
}): JSX.Element {
	const categories: KanbanCategory<number>[] = React.useMemo(() => [...props.statuses]
		.sort((a, b) => a.sequence - b.sequence)
		.map(s => ({ id: s.id, name: s.name })), [props.statuses]);

	const items: KanbanItem<number, OpportunityRow>[] = React.useMemo(() => props.opportunities.map(o => ({
		id: o.id.toString(),
		categoryId: o.opportunity_status_id,
		value: o
	})), [props.opportunities]);

	return <KanbanView<number, OpportunityRow>
		height={`calc(100vh - ${TOOLBARS_HEIGHT}px)`}
		items={items}
		categories={categories}
		dragPermission={{ objectAction: 'UpdateOpportunity' }}
		onItemClick={(_, item) => props.onOpen(item.value.id)}
		itemDisplayer={React.useCallback(item => <ItemDisplayer item={item.value} />, [])}
		onItemCategoryChange={async(item, statusId) => {
			// as in the table and popup, moving to a status applies its probability
			const probability = props.statuses.find(s => s.id === statusId)?.probability;
			// the parent re-reads the row, which re-renders the board; on failure it reloads to undo the move
			await props.updateOpportunity(item.value.id, { opportunity_status_id: statusId, probability }).catch(props.onUpdateError);
			return undefined;
		}}
	/>;
}

const ItemTitle = styled.h6`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	margin: 0;
`;

function ItemDisplayer(props: { item: OpportunityRow }) {
	const { item } = props;
	return <FlexDiv flow='column' align='stretch' gap='5px'>
		<FlexDiv justify='space-between' gap='5px'>
			<ItemTitle>{item.title}</ItemTitle>
			<ProbabilityBadge probability={item.probability} />
		</FlexDiv>
		<div>{item.client_company_name}</div>
		<FlexDiv justify='space-between' gap='5px'>
			<DateAccessor date={item.deadline} />
			<div>{formatCurrency(item.revenue)}</div>
		</FlexDiv>
		<UserBlock userId={item.owner_id} />
	</FlexDiv>;
}
